import { createSelector } from 'reselect';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';

import { currencyCode as geoCurrencyCode } from 'Helpers/Geo/Country';
import calculationSelector, {
  cartStateSelector,
  pickAndMixAllSelector,
  reportOrderCalcError,
} from 'Redux/reselects/order-calc/calculation-selector';
import { isActivePickAndMix } from 'Redux/reselects/order-calc/normalise-pick-and-mixes';
import { toUnits } from 'Redux/reselects/order-calc/cart-readers';
import { lineViews } from 'Redux/reselects/order-calc/line-view';

/**
 * What the cart page lists (Stage 7.4): the cart items in display order, and
 * the pick and mix groups (a tier, or an upsell prompt). The module applies
 * the tiers but makes no UI groups and no item order (spec §10, decision D6),
 * so this is frontend display logic, from the module lines and the basket.
 *
 * The items are the basket items (state.cart.items). The prices of an item are
 * in its line view (line-view.js), not on the item. As the old adapter did, the
 * list has one entry for each unit (a product with quantity 2 is there twice,
 * the same object): consumers remove the duplicates by id, and `items.length`
 * counts units.
 */

// Post-promo, pre-pick-and-mix line price
const promoPrice = line => line.pricing.listPrice - line.pricing.promoDiscountAmount;

// Pre-voucher line price (see line-view.js)
const preVoucherPrice = line => line.paid.total + (line.voucher?.discountAmount ?? 0);

// Legacy cartcalc.js:533-540: pick and mix groups together, newest first.
// `entries` are { item, added, pickAndMix: { slug } | undefined }.
const sortEntries = (entries) => {
  const addedOrder = orderBy(entries, ['added'], ['desc']);
  const grouped = groupBy(addedOrder, 'pickAndMix.slug');
  const merged = [
    ...(grouped.undefined || []),
    ...Object.keys(grouped).filter(key => key !== 'undefined').map(key => grouped[key]),
  ];
  // `merged` has the standalone entries (objects) and the pick and mix groups
  // (arrays): a group sorts by its newest entry, which is its first
  const sortedMerged = orderBy(
    merged,
    [group => (Array.isArray(group) ? group[0]?.added : group.added) ?? 0],
    ['desc'],
  );

  return sortedMerged.map(group => (Array.isArray(group) ? group : [group])).flat();
};

// The cart when the module could not calculate it: the items with zero
// prices, as the error cart. The price components read item.price and
// item.fullPrice directly for some types (a non-SRP bundle), so the items
// have zero prices there too, no current discount and no discount badge.
const errorItems = (cart, currencyCode) => (cart?.items ?? []).filter(Boolean).flatMap((item) => {
  const units = toUnits(item.quantity);
  const errorItem = {
    ...item,
    quantity: units,
    price: { [currencyCode]: 0 },
    fullPrice: { [currencyCode]: 0 },
    current_discount: null,
    hideDiscount: true,
  };

  return Array(units).fill(errorItem);
});

/**
 * @param   {Object}   calculation  { order, error, now }, from calculationSelector.
 * @param   {Object}   cart         state.cart
 * @param   {Object[]} pickAndMixAll state.pickAndMix.all
 * @param   {string}   currencyCode ISO 4217
 * @returns {Object}                { items, pickAndMixes }
 *   `items`: the basket items in display order, one entry for each unit.
 *   `pickAndMixes`: one group for each active promotion that holds an item of
 *   the cart: { name, slug, itemMatches, isUpsell, type, tiers, valid_until,
 *   fullPrice, tierPrice }. Prices in minor units. `isUpsell` is true when no
 *   item of the cart is in a tier of the promotion yet: `itemMatches` then
 *   holds the items that could join it.
 */
export const cartDisplay = (calculation, cart, pickAndMixAll, currencyCode) => {
  const { order } = calculation;

  if (!order) return { items: errorItems(cart, currencyCode), pickAndMixes: [] };

  const views = lineViews(calculation, cart, currencyCode, pickAndMixAll);
  const itemById = new Map((cart?.items ?? []).filter(Boolean)
    .map(item => [String(item._id), item]));
  const activePickAndMixes = (pickAndMixAll ?? [])
    .filter(pickAndMix => isActivePickAndMix(pickAndMix, calculation.now));
  const voucherApplied = !!order.voucher?.applied;
  const isIncluded = line => voucherApplied && !!line.voucher?.included;

  // The item of each line, and the first unit of each product (the view)
  const lineItems = order.lines.map(line => ({ item: itemById.get(line.itemId), line }));
  const firstLineByItem = new Map();

  lineItems.forEach(({ item, line }) => {
    if (!firstLineByItem.has(item)) firstLineByItem.set(item, line);
  });

  // Legacy sorts by the pre-pick-and-mix price before it groups, when there
  // are pick and mix promotions (cartcalc.js:345). orderBy is stable.
  const preSorted = (pickAndMixAll ?? []).length
    ? orderBy(lineItems, [({ line }) => promoPrice(line)], ['desc'])
    : lineItems;
  const items = sortEntries(preSorted.map(({ item }) => ({
    item,
    added: item.added,
    pickAndMix: views[String(item._id)]?.pickAndMix && {
      slug: views[String(item._id)].pickAndMix.slug,
    },
  }))).map(entry => entry.item);

  const pickAndMixes = activePickAndMixes.map((pickAndMix) => {
    const id = String(pickAndMix._id);
    const productIds = (pickAndMix.products ?? []).map(product => String(product?._id ?? product));
    // All units of a product are in the same promotion (spec §6 step 3)
    const tierItems = items
      .filter(item => firstLineByItem.get(item).pricing.pickAndMix?.promotionId === id);
    const tierLines = order.lines.filter(line => line.pricing.pickAndMix?.promotionId === id);
    const isUpsell = tierItems.length === 0;
    // Items in no tier count toward the upsell of each promotion that holds them
    const itemMatches = isUpsell
      ? items.filter(item => productIds.includes(String(item._id))
        && !views[String(item._id)]?.pickAndMix)
      : tierItems;

    if (itemMatches.length === 0) return null;

    return {
      name: pickAndMix.name,
      slug: pickAndMix.slug,
      itemMatches,
      isUpsell,
      type: pickAndMix.type,
      tiers: pickAndMix.tiers ?? [],
      valid_until: pickAndMix.valid_until,
      fullPrice: tierItems.reduce((sum, item) => sum + views[String(item._id)].price, 0),
      // The voucher price of included lines, else the tier share. From the
      // lines, so that it is exact for every unit.
      tierPrice: tierLines.reduce(
        (sum, line) => sum + (isIncluded(line) ? line.paid.total : preVoucherPrice(line)),
        0,
      ),
    };
  }).filter(Boolean);

  return { items, pickAndMixes };
};

// The display is not the calculation: if it throws (a basket item the display
// code does not expect), the cart page shows the items with zero prices and the
// error is reported, but the calculation, and so the payment, is not stopped.
export const cartDisplaySelector = createSelector(
  calculationSelector,
  cartStateSelector,
  pickAndMixAllSelector,
  (calculation, cart, pickAndMixAll) => {
    try {
      return cartDisplay(calculation, cart, pickAndMixAll, geoCurrencyCode);
    } catch (error) {
      reportOrderCalcError(error, { source: 'cart-display' });

      return cartDisplay({ order: null, error }, cart, pickAndMixAll, geoCurrencyCode);
    }
  },
);

export const cartItemsSelector = createSelector(cartDisplaySelector, display => display.items);

export const pickAndMixGroupsSelector = createSelector(
  cartDisplaySelector,
  display => display.pickAndMixes,
);
